import React from 'react';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import {
    RobotOutlined,
    RightOutlined,
} from '@ant-design/icons';

/**
 * AiSummaryModal Component
 * Full executive modal for AI Work Summary with clickable links to tasks, projects, and milestones.
 */
export default function AiSummaryModal({
    visible,
    onClose,
    summaryData,
    period,
    onTaskClick,
    onProjectClick,
    onMilestoneClick,
    onSprintClick,
}) {
    if (!visible) return null;

    const activeTasks = summaryData?.activeTaskCount ?? 0;
    const projectCount = summaryData?.projectCount ?? 0;
    const inProgressCount = summaryData?.inProgressCount ?? 0;
    const underReviewCount = summaryData?.underReviewCount ?? 0;
    const backlogCount = summaryData?.backlogCount ?? 0;
    const effortDuration = summaryData?.effortDuration || '0h 0m';
    const estimatedDuration = summaryData?.estimatedDuration || '0h 0m';
    const remainingDuration = summaryData?.remainingDuration || '0h 0m';

    const formattedTimestamp = summaryData?.generatedAt
        ? `Generated ${dayjs(summaryData.generatedAt).format('MMM D, YYYY, h:mm A')}`
        : `Updated ${summaryData?.updatedTimeAgo || 'Just now'}`;

    const fallbackNarrative = activeTasks === 0 ? [
        {
            type: 'overview',
            text: 'You currently have no active tasks assigned across any project for this week.',
        },
        {
            type: 'status',
            text: 'All queues are clear. When tasks are assigned to you, your work intelligence briefing will update here automatically.',
        },
        {
            type: 'effort',
            text: `${effortDuration} of actual effort has been recorded this week.`,
        },
    ] : [
        {
            type: 'overview',
            text: `You currently have ${activeTasks} active task${activeTasks === 1 ? '' : 's'} across ${projectCount} project${projectCount === 1 ? '' : 's'}.`,
        },
        ...(inProgressCount > 0 || underReviewCount > 0 || backlogCount > 0 ? [{
            type: 'status',
            text: `${inProgressCount} task${inProgressCount === 1 ? '' : 's'} in progress, while ${underReviewCount} task${underReviewCount === 1 ? '' : 's'} are currently under review and ${backlogCount} in backlog.`,
        }] : []),
        {
            type: 'effort',
            text: `${effortDuration} of actual effort has been recorded against ${estimatedDuration} of estimated work this week.`,
        },
        ...(summaryData?.dueTodayTask ? [{
            type: 'due',
            taskName: summaryData.dueTodayTask.title,
            taskId: summaryData.dueTodayTask._id,
            text: 'is due today with pending work.',
        }] : []),
        ...(summaryData?.varianceTask ? [{
            type: 'variance',
            taskName: summaryData.varianceTask.title,
            taskId: summaryData.varianceTask._id,
            text: `has recorded ${summaryData.varianceTask.detail || 'an effort variance'}.`,
        }] : []),
    ];

    const narrative = Array.isArray(summaryData?.detailedBullets) && summaryData.detailedBullets.length > 0
        ? summaryData.detailedBullets
        : fallbackNarrative;

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={720}
            centered
            className="ai-work-summary-modal"
            title={
                <div className="flex items-center justify-between gap-3 py-1 pr-6">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-600/10 text-blue-700 flex items-center justify-center flex-shrink-0">
                            <RobotOutlined style={{ fontSize: 18 }} />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline leading-tight">
                                Personal AI Work Briefing — {period}
                            </h3>
                            <p className="text-xs text-slate-500 font-medium">
                                Factual synthesis of your workload, milestones, and effort.
                            </p>
                        </div>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                        {formattedTimestamp}
                    </span>
                </div>
            }
        >
            <div className="space-y-4 pt-3 text-sm text-slate-700 dark:text-slate-300">
                {/* Metric Quick-Pills */}
                <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 dark:bg-[#1a202c] rounded-xl border border-slate-200/70 dark:border-slate-800">
                    <div>
                        <span className="text-[11px] text-slate-500 font-medium block">Active Tasks</span>
                        <span className="text-lg font-black text-slate-900 dark:text-white font-headline">
                            {activeTasks}
                        </span>
                    </div>
                    <div>
                        <span className="text-[11px] text-slate-500 font-medium block">Weekly Effort</span>
                        <span className="text-lg font-black text-emerald-600 dark:text-emerald-400 font-headline">
                            {effortDuration}
                        </span>
                    </div>
                    <div>
                        <span className="text-[11px] text-slate-500 font-medium block">Remaining Work</span>
                        <span className="text-lg font-black text-amber-600 dark:text-amber-400 font-headline">
                            {remainingDuration}
                        </span>
                    </div>
                </div>

                {/* Narrative Section */}
                <div className="space-y-3 pt-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 font-headline">
                        Work Intelligence Briefing
                    </h4>
                    <div className="space-y-2.5">
                        {narrative.map((item, idx) => {
                            const isAmber = item.type === 'variance' || item.type === 'overdue' || item.type === 'deadline_change';
                            const isSlate = item.type === 'sprint';
                            const dotColor = isAmber ? 'bg-amber-500' : isSlate ? 'bg-slate-400 dark:bg-slate-500' : 'bg-blue-600';

                            const textContent = item.taskName && item.text?.startsWith(item.taskName)
                                ? item.text.slice(item.taskName.length).trim()
                                : item.text;

                            return (
                                <div
                                    key={idx}
                                    className="flex items-start gap-3 p-2.5 rounded-lg bg-white dark:bg-[#2d3748] border border-slate-100 dark:border-slate-700/60 shadow-xs"
                                >
                                    <span className={`inline-block w-2 h-2 rounded-full ${dotColor} mt-2 flex-shrink-0`} />
                                    <div className="text-xs leading-relaxed flex-1">
                                        {/* Task Click */}
                                        {item.taskName ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onClose();
                                                        if (item.taskId) onTaskClick?.(item.taskId);
                                                    }}
                                                    className="font-bold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 mr-1 cursor-pointer"
                                                >
                                                    {item.taskName}
                                                    <RightOutlined style={{ fontSize: 9 }} />
                                                </button>
                                                {textContent}
                                            </>
                                        ) : item.projectName && onProjectClick ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onClose();
                                                        onProjectClick(item.projectId || item.projectName);
                                                    }}
                                                    className="font-bold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 mr-1 cursor-pointer"
                                                >
                                                    [{item.projectName}]
                                                </button>
                                                {textContent}
                                            </>
                                        ) : item.sprintName && onSprintClick ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onClose();
                                                        onSprintClick(item.sprintName);
                                                    }}
                                                    className="font-bold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 mr-1 cursor-pointer"
                                                >
                                                    [{item.sprintName}]
                                                </button>
                                                {textContent}
                                            </>
                                        ) : (
                                            textContent
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Note on Objective Assessment */}
                <div className="text-[11px] text-slate-400 italic bg-blue-50/50 dark:bg-blue-950/20 p-2.5 rounded-lg border border-blue-100 dark:border-blue-900/30">
                    Note: This summary reflects actual ERP task, sprint, and time-tracking metrics. Formal performance evaluations remain within the dedicated Performance Evaluation module.
                </div>
            </div>
        </Modal>
    );
}
